/**
 * API client — axios bilan o'ralgan singleton
 *
 * Xususiyatlar:
 *   - Base URL (env'dan)
 *   - Request interceptor: JWT token avtomatik qo'shish (auth store'dan)
 *   - Response interceptor: ApiSuccess data'sini extract qilish
 *   - Error interceptor: 401 → logout signal; ApiError shape → ApiClientError
 *
 * Faza 4 da AuthStore bilan to'liq integratsiya qilinadi.
 */

import type { ApiError, ApiResponse, ApiSuccess } from '@avtofix/shared';
import { isApiError } from '@avtofix/shared';
import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '');

/**
 * apiClientErrorFromUnknown — axios error → ApiClientError (agar API shape bo'lsa).
 *
 * Bu pure helper. Interceptor uchun, retry logic uchun va testlar uchun ham
 * ishlatiladi. Network error / non-API body bo'lsa null qaytaradi —
 * shu holda asl xato propagate qilinadi.
 */
export function apiClientErrorFromUnknown(error: unknown): ApiClientError | null {
  if (!axios.isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  if (data === null || typeof data !== 'object') return null;
  if (!isApiError(data as ApiResponse<unknown>)) return null;
  return new ApiClientError(data as ApiError);
}

export class ApiClient {
  private readonly client: AxiosInstance;
  private token: string | null = null;
  /** EGASI tanlagan joriy do'kon (RLS scoping). null = "Hamma do'kon". */
  private activeStoreId: string | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.client = axios.create({
      baseURL,
      timeout: 30_000,
      // Default Content-Type qo'ymaymiz: axios JSON body uchun avtomat
      // 'application/json' qo'yadi, FormData uchun esa 'multipart/form-data'
      // bilan boundary qo'shadi. Agar bu yerda 'application/json' qo'yilsa,
      // FormData boundary yo'qoladi va server faylni parse qila olmaydi (400).
    });

    this.setupInterceptors();
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  /** EGASI joriy do'konni o'rnatish. null/'all' → header yuborilmaydi (hamma do'kon). */
  setActiveStoreId(storeId: string | null): void {
    this.activeStoreId = storeId !== null && storeId !== 'all' ? storeId : null;
  }

  private setupInterceptors(): void {
    // Request: JWT qo'shish
    this.client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      // EGASI joriy do'kon scoping (server faqat EGASI uchun qo'llaydi)
      if (this.activeStoreId !== null) {
        config.headers['X-Active-Store-Id'] = this.activeStoreId;
      }
      return config;
    });

    // Response: 401 → logout signal; har qanday API-shape xato → ApiClientError
    this.client.interceptors.response.use(
      (response) => response,
      (error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          // Faza 4 da auth store'ga signal yuborish
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        const apiErr = apiClientErrorFromUnknown(error);
        if (apiErr !== null) throw apiErr;
        throw error;
      },
    );
  }

  /**
   * Generic GET — `data` ni extract qiladi (ApiSuccess.data)
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<ApiResponse<T>>(url, config);
    return this.extractData(response.data);
  }

  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<ApiResponse<T>>(url, body, config);
    return this.extractData(response.data);
  }

  async put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<ApiResponse<T>>(url, body, config);
    return this.extractData(response.data);
  }

  async patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<ApiResponse<T>>(url, body, config);
    return this.extractData(response.data);
  }

  async delete<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    // DELETE endpoint'lar 204 No Content qaytaradi (bo'sh body). Bo'sh
    // body'ni extractData → isApiError('') → "'error' in ''" TypeError
    // bo'lardi → server muvaffaqiyatli o'chirsa ham client soxta xato.
    // 204 / bo'sh / null / '' → void (parse qilmaymiz).
    const data: unknown = response.data;
    if (
      response.status === 204 ||
      data === undefined ||
      data === null ||
      data === '' ||
      typeof data !== 'object'
    ) {
      return undefined as T;
    }
    return this.extractData(response.data);
  }

  /**
   * ApiSuccess.data ni extract qilish, xato bo'lsa throw
   */
  private extractData<T>(response: ApiResponse<T>): T {
    if (isApiError(response)) {
      throw new ApiClientError(response);
    }
    return response.data;
  }

  /**
   * Pagination response uchun (data + meta)
   */
  async getPaginated<T>(url: string, config?: AxiosRequestConfig): Promise<ApiSuccess<T[]>> {
    const response = await this.client.get<ApiResponse<T[]>>(url, config);
    if (isApiError(response.data)) {
      throw new ApiClientError(response.data);
    }
    return response.data;
  }
}

/**
 * ApiClientError — server'dan kelgan ApiError'ni JS error sifatida wrap qiladi
 */
export class ApiClientError extends Error {
  readonly code: string;
  readonly statusCode: number;
  readonly details: unknown;
  readonly field?: string | undefined;

  constructor(apiError: ApiError) {
    super(apiError.error.message);
    this.name = 'ApiClientError';
    this.code = apiError.error.code;
    this.statusCode = apiError.statusCode;
    this.details = apiError.error.details;
    this.field = apiError.error.field;
  }
}

export const apiClient = new ApiClient();
